import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CampaignIcon from "@mui/icons-material/Campaign";
import SendIcon from "@mui/icons-material/Send";
import PreviewIcon from "@mui/icons-material/Preview";
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  setDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import TopHeader from "../../../Components/TopHeader/TopHeader";
import { DATABASE, FUNCTIONS } from "../../../Config/firebase";
import { emptyCampaign, isEmptyRichHtml } from "../../../Utils/campaignBlocks";
import {
  newCustomDateId,
  isIndividualClient,
  mergeClientDateReminderConfig,
} from "../../../Utils/clientDateReminders";
import { saveUserBirthDate } from "../../../services/kycService";
import BlockEditor from "./components/BlockEditor";
import TemplateLibraryDialog from "./components/TemplateLibraryDialog";
import UpcomingDatesTab from "./components/UpcomingDatesTab";
import DateReminderSettingsTab from "./components/DateReminderSettingsTab";
import CustomDateDialog from "./components/CustomDateDialog";

const DRAFT_KEY = "cc_draft_v1";

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function tsToDate(ts) {
  if (!ts) return null;
  try {
    return ts.toDate ? ts.toDate() : new Date(ts);
  } catch {
    return null;
  }
}

function formatTs(ts) {
  const d = tsToDate(ts);
  if (!d || Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", { timeZone: "Europe/London" });
}

function isSendOpened(send) {
  return send?.opened === true || Boolean(send?.opened_at);
}

function formatOpenDelay(sentAt, openedAt) {
  const sent = tsToDate(sentAt);
  const opened = tsToDate(openedAt);
  if (!sent || !opened) return "—";
  const ms = opened.getTime() - sent.getTime();
  if (ms < 0) return "< 1 min";
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return totalSec <= 1 ? "< 1 min" : `${seconds}s`;
}

function countOpenedSends(sends) {
  return (sends || []).filter((s) => s.status === "sent" && isSendOpened(s)).length;
}

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return emptyCampaign();
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.blocks)) return emptyCampaign();
    return {
      subject: parsed.subject || "",
      eyebrow: parsed.eyebrow || "CLIENT UPDATE",
      blocks: parsed.blocks,
    };
  } catch {
    return emptyCampaign();
  }
}

function campaignHasContent(campaign) {
  return (campaign.blocks || []).some((b) => {
    switch (b.type) {
      case "hero":
      case "image":
        return Boolean(b.url);
      case "heading":
        return Boolean(b.title || b.lead || b.eyebrow);
      case "greeting":
        return b.enabled !== false;
      case "text":
      case "note":
        return !isEmptyRichHtml(b.html);
      case "section":
        return Boolean(b.title) || !isEmptyRichHtml(b.html);
      case "facts":
        return (b.items || []).some((it) => it.label || it.value || it.note);
      case "button":
        return Boolean(b.label);
      case "divider":
        return true;
      default:
        return false;
    }
  });
}

const fieldSx = {
  mb: 2,
  "& .MuiInputBase-root": { color: "#fff" },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
};

const ClientCommunications = () => {
  const [tab, setTab] = useState(0);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);

  const [campaign, setCampaign] = useState(loadDraft);
  const [audienceType, setAudienceType] = useState("manual");
  const [selectedIds, setSelectedIds] = useState([]);
  const excludedIds = [];
  const [userSearch, setUserSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [serverPreviewHtml, setServerPreviewHtml] = useState("");
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [detailSends, setDetailSends] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [dateReminderConfig, setDateReminderConfig] = useState(null);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customDialogInitial, setCustomDialogInitial] = useState(null);
  const [customSaving, setCustomSaving] = useState(false);
  const [dobEdits, setDobEdits] = useState({});
  const [dobSavingId, setDobSavingId] = useState(null);

  const onDateConfigChange = useCallback((cfg) => {
    setDateReminderConfig(cfg);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(campaign));
    } catch {
      /* ignore quota */
    }
  }, [campaign]);

  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const q = query(collection(DATABASE, "users"), where("role", "==", 1));
      const snap = await getDocs(q);
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => u.email && !String(u.email).toLowerCase().endsWith("@equitysoft.in"))
        .sort((a, b) => String(a.full_name || "").localeCompare(String(b.full_name || "")));
      setClients(list);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(DATABASE, "admin_settings", "config"));
        if (cancelled) return;
        const raw = snap.exists() ? snap.data()?.client_date_reminders : null;
        setDateReminderConfig(mergeClientDateReminderConfig(raw));
      } catch (e) {
        console.warn("date reminder config load", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const q = query(collection(DATABASE, "email_campaigns"), orderBy("created_at", "desc"), limit(50));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setCampaigns(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingCampaigns(false);
      },
      (err) => {
        console.error(err);
        setLoadingCampaigns(false);
      }
    );
    return () => unsub();
  }, []);

  const activeClients = useMemo(
    () => clients.filter((c) => Number(c.status) === 1 && c.do_not_contact !== true),
    [clients]
  );

  const missingDob = useMemo(
    () =>
      clients.filter(
        (c) =>
          Number(c.status) === 1 &&
          isIndividualClient(c) &&
          !c.birth_date &&
          c.email &&
          !String(c.email).toLowerCase().endsWith("@equitysoft.in")
      ),
    [clients]
  );

  const individualClients = useMemo(
    () =>
      clients.filter(
        (c) => Number(c.status) === 1 && isIndividualClient(c) && Number(c.role) === 1
      ),
    [clients]
  );

  const filteredClients = useMemo(() => {
    const s = userSearch.trim().toLowerCase();
    if (!s) return activeClients;
    return activeClients.filter(
      (c) =>
        String(c.full_name || "").toLowerCase().includes(s) ||
        String(c.email || "").toLowerCase().includes(s)
    );
  }, [activeClients, userSearch]);

  const handleSaveDob = async (clientId) => {
    const value = (dobEdits[clientId] || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      toast.warning("Use ISO date YYYY-MM-DD");
      return;
    }
    setDobSavingId(clientId);
    try {
      const saved = await saveUserBirthDate(clientId, value);
      setClients((prev) =>
        prev.map((c) => (c.id === clientId ? { ...c, birth_date: saved || value } : c))
      );
      setDobEdits((prev) => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });
      toast.success("Date of birth saved (profile + KYC)");
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Failed to save DOB");
    } finally {
      setDobSavingId(null);
    }
  };

  const handleMuteType = async (userId, type) => {
    const client = clients.find((c) => c.id === userId);
    if (!client) return;
    const muted = { ...(client.date_reminder_muted || {}), [type]: true };
    try {
      await setDoc(doc(DATABASE, "users", userId), { date_reminder_muted: muted }, { merge: true });
      setClients((prev) =>
        prev.map((c) => (c.id === userId ? { ...c, date_reminder_muted: muted } : c))
      );
      toast.success("Reminders muted for this client/type");
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Mute failed");
    }
  };

  const handleSaveCustomDate = async (payload) => {
    const { user_id, id, label, month, day, enabled } = payload;
    const client = clients.find((c) => c.id === user_id);
    if (!client) {
      toast.warning("Select a client");
      return;
    }
    setCustomSaving(true);
    try {
      const list = Array.isArray(client.commemorative_dates)
        ? [...client.commemorative_dates]
        : [];
      const entry = {
        id: id || newCustomDateId(),
        label,
        month,
        day,
        enabled: enabled !== false,
      };
      const idx = list.findIndex((x) => x.id === entry.id);
      if (idx >= 0) list[idx] = entry;
      else list.push(entry);
      await setDoc(
        doc(DATABASE, "users", user_id),
        { commemorative_dates: list },
        { merge: true }
      );
      setClients((prev) =>
        prev.map((c) => (c.id === user_id ? { ...c, commemorative_dates: list } : c))
      );
      setCustomDialogOpen(false);
      setCustomDialogInitial(null);
      toast.success("Custom date saved");
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Save failed");
    } finally {
      setCustomSaving(false);
    }
  };

  const estimatedRecipients = useMemo(() => {
    if (audienceType === "all_active") {
      return activeClients.filter((c) => !excludedIds.includes(c.id));
    }
    return activeClients.filter((c) => selectedIds.includes(c.id) && !excludedIds.includes(c.id));
  }, [audienceType, activeClients, selectedIds, excludedIds]);

  const previewName = estimatedRecipients[0]?.full_name || "Client";

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredClients.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredClients.map((c) => c.id));
    }
  };

  const toggleUser = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const assertComposeReady = () => {
    if (!campaign.subject.trim()) {
      toast.warning("Subject is required");
      return false;
    }
    if (!campaignHasContent(campaign)) {
      toast.warning("Add at least one content block");
      return false;
    }
    return true;
  };

  const payloadBase = () => ({
    email_subject: campaign.subject.trim(),
    blocks: campaign.blocks,
    eyebrow: campaign.eyebrow || "CLIENT UPDATE",
    preview_name: previewName,
  });

  const handleServerPreview = async () => {
    if (!assertComposeReady()) return;
    setPreviewing(true);
    try {
      const fn = httpsCallable(FUNCTIONS, "renderCampaignPreview");
      const res = await fn(payloadBase());
      setServerPreviewHtml(res.data?.html || "");
      setPreviewOpen(true);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Server preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const handleTestSend = async () => {
    if (!assertComposeReady()) return;
    const confirm = await Swal.fire({
      title: "Send test to yourself?",
      html: `<p>This emails only your admin inbox.</p>
             <p style="font-size:13px;color:#666">${campaign.subject.trim()}</p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2af598",
      confirmButtonText: "Send test",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setTesting(true);
    try {
      const fn = httpsCallable(FUNCTIONS, "sendCampaignTest");
      const res = await fn(payloadBase());
      toast.success(`Test sent to ${res.data?.to || "your email"}`);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  const handleSendCampaign = async () => {
    if (!assertComposeReady()) return;
    if (audienceType === "manual" && selectedIds.length === 0) {
      toast.warning("Select at least one client");
      return;
    }
    const count = estimatedRecipients.length;
    if (count === 0) {
      toast.warning("No eligible recipients");
      return;
    }

    const confirm = await Swal.fire({
      title: "Send campaign?",
      html: `<p>This will email <strong>${count}</strong> client(s).</p>
             <p style="font-size:13px;color:#666">${campaign.subject.trim()}</p>
             <p style="font-size:12px;color:#999">Audience: ${audienceType === "all_active" ? "All active clients" : "Manual selection"}</p>
             <p style="font-size:12px;color:#c0392b;margin-top:12px"><strong>Only send after founder approval.</strong> Do not use this during the rebuild.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2af598",
      confirmButtonText: `Yes, send to ${count}`,
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setSending(true);
    try {
      const fn = httpsCallable(FUNCTIONS, "sendCampaign");
      const res = await fn({
        ...payloadBase(),
        audience_type: audienceType,
        manual_user_ids: audienceType === "manual" ? selectedIds : [],
        excluded_user_ids: excludedIds,
      });
      const counts = res.data?.counts || {};
      toast.success(
        `Campaign ${res.data?.status || "sent"}: ${counts.sent || 0} sent, ${counts.failed || 0} failed`
      );
      setTab(1);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Campaign send failed");
    } finally {
      setSending(false);
    }
  };

  const openCampaignDetail = async (row) => {
    setDetailCampaign(row);
    setDetailOpen(true);
    setLoadingDetail(true);
    try {
      const snap = await getDocs(collection(DATABASE, "email_campaigns", row.id, "sends"));
      const sends = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(a.full_name || "").localeCompare(String(b.full_name || "")));
      setDetailSends(sends);

      const openedFromSends = countOpenedSends(sends);
      const storedOpened = row.counts?.opened || 0;
      if (openedFromSends !== storedOpened) {
        const nextCounts = { ...(row.counts || {}), opened: openedFromSends };
        await setDoc(doc(DATABASE, "email_campaigns", row.id), { counts: nextCounts }, { merge: true });
        setDetailCampaign((prev) => (prev ? { ...prev, counts: nextCounts } : prev));
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load sends");
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <TopHeader title="Client Communications" />
      <Paper
        sx={{
          mt: 1,
          p: 2,
          bgcolor: "#14141f",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CampaignIcon sx={{ color: "#2af598" }} />
          <Typography sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 600 }}>
            CRM Lite — institutional announcements for Fiddux clients
          </Typography>
        </Box>
        <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 13, mb: 1 }}>
          Active clients: {activeClients.length} · Missing DOB (individuals): {missingDob.length} ·
          Kill-switch in Admin Settings → Campaign Emails · Date digests → Settings tab
        </Typography>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="inherit"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            "& .MuiTab-root": { color: "rgba(255,255,255,0.55)", textTransform: "none" },
            "& .Mui-selected": { color: "#2af598 !important" },
            "& .MuiTabs-indicator": { bgcolor: "#2af598" },
          }}
        >
          <Tab label="Compose" />
          <Tab label={`History (${campaigns.length})`} />
          <Tab label="Upcoming" />
          <Tab label={`Missing DOB (${missingDob.length})`} />
          <Tab label="Date settings" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setTemplatesOpen(true)}
              sx={{ borderColor: "#7eb8ff", color: "#b8d4ff" }}
            >
              Templates
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={previewing ? <CircularProgress size={14} /> : <PreviewIcon />}
              disabled={previewing || testing || sending}
              onClick={handleServerPreview}
              sx={{ borderColor: "rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.85)" }}
            >
              {previewing ? "Rendering…" : "Preview"}
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={testing || sending}
              onClick={handleTestSend}
              sx={{ bgcolor: "#7eb8ff", color: "#0a1a12", "&:hover": { bgcolor: "#9dcaff" } }}
            >
              {testing ? "Sending test…" : "Send test to me"}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={sending ? <CircularProgress size={14} /> : <SendIcon />}
              disabled={testing || sending}
              onClick={handleSendCampaign}
              sx={{ bgcolor: "#2af598", color: "#0a1a12", "&:hover": { bgcolor: "#3dffb0" } }}
            >
              {sending ? "Sending…" : `Send to ${estimatedRecipients.length}`}
            </Button>
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Subject"
            value={campaign.subject}
            onChange={(e) => setCampaign((prev) => ({ ...prev, subject: e.target.value }))}
            sx={fieldSx}
          />
          <TextField
            fullWidth
            size="small"
            label="Header eyebrow"
            value={campaign.eyebrow}
            onChange={(e) => setCampaign((prev) => ({ ...prev, eyebrow: e.target.value }))}
            sx={fieldSx}
          />

          <BlockEditor
            blocks={campaign.blocks}
            onChange={(blocks) => setCampaign((prev) => ({ ...prev, blocks }))}
            eyebrow={campaign.eyebrow}
            previewName={previewName}
          />

          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 2 }} />

          <Typography sx={{ color: "#2af598", fontSize: 12, fontWeight: 700, letterSpacing: 1, mb: 1 }}>
            AUDIENCE
          </Typography>
          <FormControl>
            <RadioGroup
              row
              value={audienceType}
              onChange={(e) => setAudienceType(e.target.value)}
            >
              <FormControlLabel
                value="all_active"
                control={<Radio sx={{ color: "rgba(255,255,255,0.5)", "&.Mui-checked": { color: "#2af598" } }} />}
                label={<Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 14 }}>All active clients ({activeClients.length})</Typography>}
              />
              <FormControlLabel
                value="manual"
                control={<Radio sx={{ color: "rgba(255,255,255,0.5)", "&.Mui-checked": { color: "#2af598" } }} />}
                label={<Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 14 }}>Manual selection</Typography>}
              />
            </RadioGroup>
          </FormControl>

          {audienceType === "manual" && (
            <Box sx={{ mt: 1 }}>
              <TextField
                size="small"
                placeholder="Search clients…"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "rgba(255,255,255,0.4)", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1,
                  minWidth: 280,
                  "& .MuiInputBase-root": { color: "#fff" },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
                }}
              />
              <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                <Button size="small" onClick={toggleSelectAll} sx={{ color: "#7eb8ff", textTransform: "none" }}>
                  {selectedIds.length === filteredClients.length ? "Deselect all" : "Select all filtered"}
                </Button>
                <Chip size="small" label={`${selectedIds.length} selected`} sx={{ color: "#fff" }} />
              </Box>
              <Paper
                sx={{
                  maxHeight: 260,
                  overflow: "auto",
                  bgcolor: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  p: 1,
                }}
              >
                {loadingClients ? (
                  <CircularProgress size={22} sx={{ m: 2 }} />
                ) : (
                  filteredClients.map((c) => (
                    <FormControlLabel
                      key={c.id}
                      sx={{ display: "flex", m: 0, py: 0.25 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(c.id)}
                          onChange={() => toggleUser(c.id)}
                          sx={{ color: "rgba(255,255,255,0.4)", "&.Mui-checked": { color: "#2af598" } }}
                        />
                      }
                      label={
                        <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>
                          {c.full_name || "—"} <span style={{ opacity: 0.55 }}>({c.email})</span>
                          {c.do_not_contact ? (
                            <Chip size="small" label="Do not contact" color="warning" sx={{ ml: 1, height: 20, fontSize: 10 }} />
                          ) : null}
                        </Typography>
                      }
                    />
                  ))
                )}
              </Paper>
            </Box>
          )}

          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12, mt: 2 }}>
            Estimated recipients after exclusions / do-not-contact: <strong style={{ color: "#2af598" }}>{estimatedRecipients.length}</strong>
          </Typography>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          {loadingCampaigns ? (
            <CircularProgress size={24} />
          ) : campaigns.length === 0 ? (
            <Typography sx={{ color: "rgba(255,255,255,0.5)" }}>No campaigns yet.</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {campaigns.map((c) => {
                const total = c.counts?.total || 0;
                const sent = c.counts?.sent || 0;
                const opened = c.counts?.opened || 0;
                const openRate = sent > 0 ? Math.round((opened / sent) * 100) : 0;
                return (
                  <Paper
                    key={c.id}
                    sx={{
                      p: 1.5,
                      bgcolor: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      cursor: "pointer",
                      "&:hover": { borderColor: "rgba(42,245,152,0.4)" },
                    }}
                    onClick={() => openCampaignDetail(c)}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>{c.subject}</Typography>
                      <Chip
                        size="small"
                        label={c.status || "—"}
                        sx={{
                          height: 22,
                          fontSize: 11,
                          bgcolor:
                            c.status === "sent"
                              ? "rgba(42,245,152,0.15)"
                              : c.status === "partial"
                                ? "rgba(255,193,7,0.15)"
                                : "rgba(255,255,255,0.08)",
                          color: "#fff",
                        }}
                      />
                    </Box>
                    <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12, mt: 0.5 }}>
                      {formatTs(c.sent_at || c.created_at)} · by {c.created_by || "—"} · {sent}/{total} sent · {opened} opened ({openRate}%)
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <UpcomingDatesTab
            clients={individualClients}
            config={dateReminderConfig}
            onAddCustom={() => {
              setCustomDialogInitial(null);
              setCustomDialogOpen(true);
            }}
            onEditCustom={(item) => {
              const client = clients.find((c) => c.id === item.user_id);
              const cd = (client?.commemorative_dates || []).find((x) => x.id === item.custom_id);
              setCustomDialogInitial({
                user_id: item.user_id,
                custom_id: item.custom_id,
                label: cd?.label || item.label,
                month: cd?.month,
                day: cd?.day,
                enabled: cd?.enabled !== false,
              });
              setCustomDialogOpen(true);
            }}
            onMuteType={handleMuteType}
          />
        </TabPanel>

        <TabPanel value={tab} index={3}>
          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 13, mb: 1.5 }}>
            Individual clients without <code>birth_date</code>. Saving here updates the client profile
            and mirrors KYC (<code>kyc_profiles.birth_date</code>).
          </Typography>
          {missingDob.length === 0 ? (
            <Typography sx={{ color: "#2af598" }}>
              All active individual clients have a date of birth on file.
            </Typography>
          ) : (
            <Paper
              sx={{
                bgcolor: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.08)",
                overflow: "auto",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: "rgba(255,255,255,0.6)" }}>Name</TableCell>
                    <TableCell sx={{ color: "rgba(255,255,255,0.6)" }}>Email</TableCell>
                    <TableCell sx={{ color: "rgba(255,255,255,0.6)" }}>Date of birth</TableCell>
                    <TableCell sx={{ color: "rgba(255,255,255,0.6)" }} align="right">
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {missingDob.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell sx={{ color: "#fff" }}>{c.full_name || "—"}</TableCell>
                      <TableCell sx={{ color: "rgba(255,255,255,0.75)" }}>{c.email}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="date"
                          value={dobEdits[c.id] || ""}
                          onChange={(e) =>
                            setDobEdits((prev) => ({ ...prev, [c.id]: e.target.value }))
                          }
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            ...fieldSx,
                            mb: 0,
                            minWidth: 160,
                            "& input": { colorScheme: "dark" },
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          disabled={dobSavingId === c.id || !(dobEdits[c.id] || "").trim()}
                          onClick={() => handleSaveDob(c.id)}
                          sx={{ color: "#2af598", textTransform: "none" }}
                        >
                          {dobSavingId === c.id ? "Saving…" : "Save"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </TabPanel>

        <TabPanel value={tab} index={4}>
          <DateReminderSettingsTab onConfigChange={onDateConfigChange} />
        </TabPanel>
      </Paper>

      <CustomDateDialog
        open={customDialogOpen}
        onClose={() => {
          setCustomDialogOpen(false);
          setCustomDialogInitial(null);
        }}
        clients={individualClients}
        initial={customDialogInitial}
        onSave={handleSaveCustomDate}
        saving={customSaving}
      />

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Server preview</DialogTitle>
        <DialogContent dividers>
          <iframe
            title="email-server-preview"
            srcDoc={serverPreviewHtml}
            style={{ width: "100%", height: 520, border: "1px solid #ddd", borderRadius: 8 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{detailCampaign?.subject || "Campaign"}</DialogTitle>
        <DialogContent dividers>
          {loadingDetail ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12, mb: 1.5 }}>
                {(() => {
                  const delivered = detailSends.filter((s) => s.status === "sent").length;
                  const opened = countOpenedSends(detailSends);
                  const pct = delivered > 0 ? Math.round((opened / delivered) * 100) : 0;
                  return `${opened} opened of ${delivered} delivered (${pct}%). Times shown in UK (Europe/London).`;
                })()}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Delivered</TableCell>
                    <TableCell>Opened</TableCell>
                    <TableCell>Time to open</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailSends.map((s) => {
                    const opened = isSendOpened(s);
                    const deliveredAt = s.status === "sent" ? s.sent_at : null;
                    return (
                      <TableRow key={s.id}>
                        <TableCell>{s.full_name || "—"}</TableCell>
                        <TableCell>{s.email}</TableCell>
                        <TableCell>{s.status}</TableCell>
                        <TableCell>{deliveredAt ? formatTs(deliveredAt) : "—"}</TableCell>
                        <TableCell>{opened ? formatTs(s.opened_at) : "—"}</TableCell>
                        <TableCell>
                          {opened && deliveredAt ? formatOpenDelay(deliveredAt, s.opened_at) : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <TemplateLibraryDialog
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        currentCampaign={campaign}
        onLoad={(next) => setCampaign(next)}
      />
    </Box>
  );
};

export default ClientCommunications;
